import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import * as categoryService from '../../services/categoryService'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import { ImageUploadField } from '../../components/ui/ImageUploadField'
import { Modal } from '../../components/ui/Modal'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { EmptyState } from '../../components/ui/EmptyState'
import { LoadMoreFooter } from '../../components/ui/LoadMoreFooter'
import { SkeletonListPage } from '../../components/ui/Skeleton'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'
import * as storefrontAi from '../../services/storefrontAiService'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { useLoadMoreList } from '../../hooks/useLoadMoreList'
import { canUseStorefrontAi } from '../../config/staffModules'

function isCategoryPresetsSchemaError(message: string | null) {
  if (!message) return false
  return /store_categories|category_id|Unknown column|is_active/i.test(message)
}

const emptyForm = { name: '', slug: '', image: '', sortOrder: '' }

const actionBtn =
  'rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-transform active:scale-[0.98] disabled:opacity-50'

const DEFAULT_LIMIT = 25

export default function CategoryList() {
  const { user } = useAuth()
  const { pushToast } = useToast()
  const location = useLocation()
  const canUseCategoryAi = canUseStorefrontAi(user?.role, user?.modules)

  const [defaultStoreId, setDefaultStoreId] = useState<number | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  const [form, setForm] = useState(emptyForm)
  const [imageUploading, setImageUploading] = useState(false)
  const [aiImageGenerating, setAiImageGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [rowBusyId, setRowBusyId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<categoryService.CategoryRow | null>(null)

  const closeModal = useCallback(() => {
    setModalOpen(false)
    setEditingId(null)
    setForm(emptyForm)
    setSaveError(null)
    setUploadError(null)
    setAiImageGenerating(false)
  }, [])

  const skipQResetRef = useRef(true)
  const editFromNavRef = useRef<number | null>(null)

  const fetchPage = useCallback(
    async (page: number, limit: number) => {
      const res = await categoryService.fetchCategories({ page, limit, q: q || undefined })
      setDefaultStoreId(res.defaultStoreId)
      return { items: res.items, total: res.total }
    },
    [q],
  )

  const {
    items: categoryItems,
    total: listTotal,
    loading,
    loadingMore,
    error: listError,
    hasMore,
    reload,
    loadMore,
  } = useLoadMoreList<categoryService.CategoryRow>({
    fetchPage,
    limit: DEFAULT_LIMIT,
    resetDeps: [q],
  })

  const tableBusy = loadingMore
  const data = defaultStoreId != null ? { defaultStoreId, items: categoryItems, total: listTotal } : null

  useEffect(() => {
    const t = window.setTimeout(() => setQ(searchInput.trim()), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    if (skipQResetRef.current) {
      skipQResetRef.current = false
      return
    }
  }, [q])

  const refreshList = useCallback(async () => {
    await reload()
  }, [reload])

  const pollCategoryAiImageJob = async (jobId: string) => {
    try {
      const result = await storefrontAi.pollStorefrontAiImageJob(jobId, {
        maxAttempts: 180,
        intervalMs: 2000,
      })
      if (result.status === 'completed') {
        pushToast({ variant: 'success', message: 'Category image ready.' })
        await refreshList()
        return
      }
      if (result.status === 'failed') {
        pushToast({
          variant: 'error',
          message:
            result.error ||
            'Category saved. Automatic image could not be generated. You can upload an image when editing the category.',
        })
      }
    } catch (e) {
      pushToast({
        variant: 'error',
        message: getUserFacingErrorMessage(e, 'Could not load generated category image'),
      })
    }
  }

  async function handleGenerateCategoryImage() {
    if (editingId == null) return
    const name = form.name.trim()
    if (!name) {
      pushToast({ variant: 'error', message: 'Enter a category name before generating an image.' })
      return
    }
    setUploadError(null)
    setAiImageGenerating(true)
    try {
      const { jobId } = await storefrontAi.startCategoryImageJob({
        categoryId: editingId,
        categoryName: name,
        categorySlug: form.slug.trim() || null,
      })
      const result = await storefrontAi.pollStorefrontAiImageJob(jobId, {
        maxAttempts: 180,
        intervalMs: 2000,
      })
      if (result.status === 'completed') {
        setForm((f) => ({ ...f, image: result.imageUrl }))
        pushToast({
          variant: 'success',
          message: 'AI category image ready. Save changes to confirm.',
        })
        return
      }
      pushToast({
        variant: 'error',
        message:
          result.status === 'failed'
            ? result.error || 'Category image generation failed'
            : 'Category image generation timed out',
      })
    } catch (e) {
      pushToast({
        variant: 'error',
        message: getUserFacingErrorMessage(e, 'Could not generate category image'),
      })
    } finally {
      setAiImageGenerating(false)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaveError(null)
    setSaving(true)
    try {
      const sortParsed = form.sortOrder.trim() === '' ? undefined : Number.parseInt(form.sortOrder, 10)
      const sortOrder =
        sortParsed !== undefined && Number.isFinite(sortParsed) ? sortParsed : undefined

      if (editingId != null) {
        await categoryService.updateCategory(editingId, {
          name: form.name.trim(),
          slug: form.slug.trim() || undefined,
          image: form.image.trim() ? form.image.trim() : null,
          ...(sortOrder !== undefined ? { sortOrder } : {}),
        })
        pushToast({ variant: 'success', message: 'Category saved.' })
        await refreshList()
      } else {
        const created = await categoryService.createCategory({
          name: form.name.trim(),
          slug: form.slug.trim() || undefined,
          image: form.image.trim() || undefined,
          ...(sortOrder !== undefined ? { sortOrder } : {}),
        })
        pushToast({ variant: 'success', message: 'Category created.' })
        const jobId = created.aiImageJobId?.trim() ? created.aiImageJobId.trim() : null
        await refreshList()
        closeModal()
        if (jobId) {
          pushToast({ variant: 'info', message: 'Category image generation started.', duration: 3000 })
          void pollCategoryAiImageJob(jobId)
        } else if (!form.image.trim() && canUseCategoryAi) {
          void (async () => {
            try {
              const { jobId: startedJobId } = await storefrontAi.startCategoryImageJob({
                categoryId: created.id,
                categoryName: created.name,
                categorySlug: created.slug,
              })
              pushToast({ variant: 'info', message: 'Category image generation started.', duration: 3000 })
              await pollCategoryAiImageJob(startedJobId)
            } catch {
              // Category was saved; merchant can generate or upload from edit.
            }
          })()
        }
        return
      }
      closeModal()
    } catch (err) {
      setSaveError(getUserFacingErrorMessage(err, 'Failed to save category'))
    } finally {
      setSaving(false)
    }
  }

  async function handleToggleActive(row: categoryService.CategoryRow) {
    if (row.id == null) return
    setRowBusyId(row.id)
    try {
      const currentlyActive = row.isActive !== false
      await categoryService.updateCategory(row.id, { isActive: !currentlyActive })
      await refreshList()
    } catch (err) {
      pushToast({
        variant: 'error',
        message: getUserFacingErrorMessage(err, 'Could not update status'),
      })
    } finally {
      setRowBusyId(null)
    }
  }

  async function confirmDelete() {
    if (deleteTarget?.id == null) return
    const id = deleteTarget.id
    setRowBusyId(id)
    try {
      await categoryService.deleteCategory(id)
      await refreshList()
      setDeleteTarget(null)
      pushToast({ variant: 'success', message: 'Category deleted.' })
    } catch (err) {
      pushToast({
        variant: 'error',
        message: getUserFacingErrorMessage(err, 'Could not delete category'),
      })
    } finally {
      setRowBusyId(null)
    }
  }

  function openEdit(row: categoryService.CategoryRow) {
    if (row.id == null) return
    setSaveError(null)
    setUploadError(null)
    setEditingId(row.id)
    setForm({
      name: row.name,
      slug: row.slug,
      image: row.image ?? '',
      sortOrder: row.sortOrder != null && row.sortOrder !== undefined ? String(row.sortOrder) : '',
    })
    setModalOpen(true)
  }

  useEffect(() => {
    const editId = (location.state as { editCategoryId?: number } | null)?.editCategoryId
    if (editId == null || editFromNavRef.current === editId) return
    const row = data?.items.find((item) => item.id === editId)
    if (!row?.id) return
    editFromNavRef.current = editId
    openEdit(row)
  }, [location.state, categoryItems])

  const migrationWarning =
    isCategoryPresetsSchemaError(listError) || isCategoryPresetsSchemaError(saveError)
  const listErrorDestructive = Boolean(listError && !isCategoryPresetsSchemaError(listError))
  const saveErrorDestructive = Boolean(
    saveError && modalOpen && !isCategoryPresetsSchemaError(saveError),
  )

  const canManage = Boolean(data?.defaultStoreId) && !loading && !listErrorDestructive && !tableBusy
  const hasActiveSearch = searchInput.trim() !== ''

  return (
    <Card accent>
      <CardHeader
        eyebrow="Catalog"
        title="Categories"
        description="Preset groupings for your storefront: name, slug, and optional image (upload). Inactive categories are hidden on the storefront."
        action={
          <Button
            type="button"
            onClick={() => {
              setSaveError(null)
              setUploadError(null)
              setForm(emptyForm)
              setEditingId(null)
              setModalOpen(true)
            }}
            disabled={!canManage}
          >
            Add category
          </Button>
        }
      />

      <div className="mb-6 space-y-3">
        {migrationWarning ? (
          <Alert variant="warning" title="Database may need updating">
            <p>
              The API expects the <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground">store_categories</code>{' '}
              table (and <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground">products.category_id</code>
              ). From the <strong className="font-semibold text-foreground">Kartly API</strong> project, run migrations (for
              example <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground">yarn db:migrate</code>), then
              reload this page.
            </p>
            {listError && isCategoryPresetsSchemaError(listError) ? (
              <p className="mt-3 rounded-xl border border-border bg-muted/50 px-3 py-2 font-mono text-xs text-foreground">
                {listError}
              </p>
            ) : null}
            {saveError && isCategoryPresetsSchemaError(saveError) ? (
              <p className="mt-3 rounded-xl border border-border bg-muted/50 px-3 py-2 font-mono text-xs text-foreground">
                {saveError}
              </p>
            ) : null}
          </Alert>
        ) : null}
        {listErrorDestructive ? (
          <Alert variant="destructive" title="Could not load categories">
            {listError}
          </Alert>
        ) : null}
        {!data?.defaultStoreId && !loading && !listError ? (
          <Alert variant="warning" title="No store yet">
            No store was found for your organisation. Complete store onboarding before managing categories.
          </Alert>
        ) : null}
      </div>

      {loading ? (
        <SkeletonListPage columns={9} rows={8} />
      ) : listError && !data ? (
        <div className="flex min-h-[12rem] items-center justify-center rounded-2xl border border-border bg-muted/20 px-6 py-8 text-center">
          <p className="text-sm text-muted-foreground">Fix the issue above to load your category list.</p>
        </div>
      ) : !data?.defaultStoreId ? null : (
        <>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-[200px] flex-1">
              <Input
                id="category-search"
                label="Search"
                placeholder="Name or slug…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                autoComplete="off"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              className="w-full sm:w-auto"
              disabled={!hasActiveSearch}
              onClick={() => setSearchInput('')}
            >
              Clear search
            </Button>
          </div>
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm shadow-sm">
          <div className="flex flex-col gap-3 border-b border-border bg-muted/50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Current presets</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Shown on the storefront and when assigning products. Includes slug-derived rows from products.
              </p>
            </div>
            <span className="inline-flex w-fit shrink-0 rounded-full bg-card px-2.5 py-1 text-xs font-medium text-foreground ring-1 ring-border">
              {listTotal} {listTotal === 1 ? 'category' : 'categories'}
            </span>
          </div>
          {listTotal === 0 && !hasActiveSearch ? (
            <div className="p-6">
              <EmptyState
                title="No categories yet"
                description="Create your first category to organize products on your storefront."
                action={
                  <Button type="button" onClick={() => setModalOpen(true)} disabled={!canManage}>
                    Add your first category
                  </Button>
                }
              />
            </div>
          ) : listTotal === 0 && hasActiveSearch ? (
            <div className="p-6">
              <EmptyState
                title="No categories match your search"
                description="Try a different name or slug, or clear the search."
                action={
                  <Button type="button" variant="secondary" onClick={() => setSearchInput('')}>
                    Clear search
                  </Button>
                }
              />
            </div>
          ) : (
          <div className={`overflow-x-auto ${tableBusy ? 'opacity-60' : ''}`}>
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-card text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-16 px-4 py-3.5 font-semibold sm:px-5" scope="col">
                    S/N
                  </th>
                  <th className="w-20 px-4 py-3.5 font-semibold sm:px-5" scope="col" aria-label="Image" />
                  <th className="px-4 py-3.5 font-semibold sm:px-5" scope="col">
                    Slug
                  </th>
                  <th className="px-4 py-3.5 font-semibold sm:px-5" scope="col">
                    Name
                  </th>
                  <th className="px-4 py-3.5 font-semibold sm:px-5" scope="col">
                    Products
                  </th>
                  <th className="px-4 py-3.5 font-semibold sm:px-5" scope="col">
                    Order
                  </th>
                  <th className="px-4 py-3.5 font-semibold sm:px-5" scope="col">
                    Source
                  </th>
                  <th className="px-4 py-3.5 font-semibold sm:px-5" scope="col">
                    Status
                  </th>
                  <th className="px-4 py-3.5 text-right font-semibold sm:px-5" scope="col">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {categoryItems.map((row, rowIndex) => {
                  const isStore = row.source === 'store' && row.id != null
                  const isInactive = isStore && row.isActive === false
                  return (
                    <tr
                      key={`${row.source}-${row.slug}-${row.id ?? 'orphan'}`}
                      className={`transition-colors hover:bg-muted/40 ${isInactive ? 'bg-muted/30' : ''}`}
                    >
                      <td className="px-4 py-3.5 font-mono text-xs text-foreground sm:px-5">
                        {rowIndex + 1}
                      </td>
                      <td className="px-4 py-3 sm:px-5">
                        {row.image ? (
                          <img
                            src={row.image}
                            alt=""
                            className="h-12 w-12 rounded-xl border border-border object-cover shadow-sm"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 text-[10px] font-medium text-muted-foreground">
                            —
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-xs text-foreground sm:px-5">{row.slug}</td>
                      <td className="px-4 py-3.5 font-medium text-foreground sm:px-5">{row.name}</td>
                      <td className="px-4 py-3.5 tabular-nums text-foreground sm:px-5">
                        {row.productCount ?? 0}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-xs text-foreground sm:px-5">
                        {isStore ? (row.sortOrder ?? '—') : '—'}
                      </td>
                      <td className="px-4 py-3.5 sm:px-5">
                        <span className="inline-flex rounded-lg bg-muted px-2 py-0.5 text-xs font-medium capitalize text-foreground">
                          {row.source}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 sm:px-5">
                        {isStore ? (
                          <span
                            className={`inline-flex rounded-lg px-2 py-0.5 text-xs font-semibold ${
                              isInactive
                                ? 'bg-muted text-muted-foreground ring-1 ring-border'
                                : 'bg-primary/15 text-primary ring-1 ring-primary/25'
                            }`}
                          >
                            {isInactive ? 'Inactive' : 'Active'}
                          </span>
                        ) : (
                          <div className="flex flex-wrap items-center justify-end gap-1.5">
                            <Link
                              to={categoryService.categoryDetailPath(row)}
                              className={`${actionBtn} text-foreground ring-1 ring-border hover:bg-muted`}
                            >
                              View
                            </Link>
                            {(row.productCount ?? 0) > 0 ? (
                              <Link
                                to={`/products?categorySlug=${encodeURIComponent(row.slug)}`}
                                className={`${actionBtn} text-foreground ring-1 ring-border hover:bg-muted`}
                              >
                                Products
                              </Link>
                            ) : null}
                            <span className="text-xs text-muted-foreground">From products</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-right sm:px-5">
                        {isStore ? (
                          <div className="flex flex-wrap items-center justify-end gap-1.5">
                            <Link
                              to={categoryService.categoryDetailPath(row)}
                              className={`${actionBtn} text-foreground ring-1 ring-border hover:bg-muted`}
                            >
                              View
                            </Link>
                            {(row.productCount ?? 0) > 0 ? (
                              <Link
                                to={`/products?categorySlug=${encodeURIComponent(row.slug)}`}
                                className={`${actionBtn} text-foreground ring-1 ring-border hover:bg-muted`}
                              >
                                Products
                              </Link>
                            ) : null}
                            <button
                              type="button"
                              className={`${actionBtn} text-primary ring-1 ring-primary/25 hover:bg-primary/10`}
                              disabled={rowBusyId === row.id || !canManage}
                              onClick={() => openEdit(row)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className={`${actionBtn} text-foreground ring-1 ring-border hover:bg-muted`}
                              disabled={rowBusyId === row.id || !canManage}
                              onClick={() => void handleToggleActive(row)}
                            >
                              {isInactive ? 'Activate' : 'Deactivate'}
                            </button>
                            <button
                              type="button"
                              className={`${actionBtn} text-destructive ring-1 ring-destructive/25 hover:bg-destructive/10`}
                              disabled={rowBusyId === row.id || !canManage}
                              onClick={() => setDeleteTarget(row)}
                            >
                              Delete
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          )}
          <LoadMoreFooter
            shown={categoryItems.length}
            total={listTotal}
            hasMore={hasMore}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </div>
        </>
      )}

      <ConfirmModal
        open={deleteTarget != null}
        title="Delete this category?"
        variant="danger"
        size="sm"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        description={
          deleteTarget ? (
            <div className="space-y-3">
              <p>
                You are about to delete{' '}
                <strong className="font-semibold text-foreground">“{deleteTarget.name}”</strong>{' '}
                <span className="font-mono text-foreground">({deleteTarget.slug})</span>.
              </p>
              <p className="text-muted-foreground">
                This cannot be undone if no products use this category.
              </p>
            </div>
          ) : null
        }
      />

      <Modal
        open={modalOpen}
        title={editingId != null ? 'Edit category' : 'Add category'}
        onClose={closeModal}
        size="lg"
      >
        <p className="mb-5 text-sm text-muted-foreground">
          Slug is generated from the name if you leave it blank. Use a square image when possible.
        </p>
        {saveErrorDestructive ? (
          <div className="mb-4">
            <Alert variant="destructive" title="Could not save category">
              {saveError}
            </Alert>
          </div>
        ) : null}
        {uploadError ? (
          <div className="mb-4">
            <Alert variant="destructive" title="Upload failed">
              {uploadError}
            </Alert>
          </div>
        ) : null}
        {migrationWarning && saveError && isCategoryPresetsSchemaError(saveError) ? (
          <div className="mb-4">
            <Alert variant="warning" title="Database may need updating">
              Run <code className="rounded bg-muted px-1 font-mono text-xs text-foreground">yarn db:migrate</code> in{' '}
              <strong className="font-semibold">Kartly API</strong>, then try again.
            </Alert>
          </div>
        ) : null}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            id="modal-cat-name"
            label="Display name *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            placeholder="e.g. Apparel"
          />
          <Input
            id="modal-cat-slug"
            label="Slug (optional)"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            placeholder="auto from name"
          />
          <Input
            id="modal-cat-sort"
            label="Display order (optional)"
            value={form.sortOrder}
            onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
            placeholder="e.g. 0 — lower first on storefront"
            inputMode="numeric"
          />
          {form.image ? (
            <div className="space-y-2">
              <span className="block text-sm font-medium text-foreground">Current image</span>
              <img
                src={form.image}
                alt=""
                className="h-32 w-32 rounded-xl border border-border object-cover shadow-sm"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={saving || imageUploading || aiImageGenerating}
                  onClick={() => setForm((f) => ({ ...f, image: '' }))}
                >
                  Remove image
                </Button>
                {canUseCategoryAi && editingId != null ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={saving || imageUploading || aiImageGenerating || !form.name.trim()}
                    onClick={() => void handleGenerateCategoryImage()}
                  >
                    {aiImageGenerating ? 'Generating AI image…' : 'Regenerate with AI'}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : aiImageGenerating ? (
            <div className="space-y-2">
              <span className="block text-sm font-medium text-foreground">Image</span>
              <div className="flex min-h-[8rem] items-center justify-center rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
                Generating AI category image…
              </div>
            </div>
          ) : null}
          <ImageUploadField
            label={form.image || aiImageGenerating ? 'Replace image' : 'Image (optional)'}
            hint="PNG, JPEG, WebP, or GIF. Upload a square tile, generate one with AI after saving, or leave empty to auto-generate on create."
            multiple={false}
            disabled={saving || aiImageGenerating}
            onUploadStateChange={setImageUploading}
            onUploadError={(msg) => setUploadError(msg)}
            onUploaded={(urls) => {
              setUploadError(null)
              if (urls[0]) {
                setForm((f) => ({ ...f, image: urls[0] }))
                pushToast({ variant: 'success', message: 'Image uploaded.' })
              }
            }}
          />
          {!form.image && !aiImageGenerating && canUseCategoryAi ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={
                  saving ||
                  imageUploading ||
                  aiImageGenerating ||
                  !form.name.trim() ||
                  editingId == null
                }
                onClick={() => void handleGenerateCategoryImage()}
              >
                Generate with AI
              </Button>
              {editingId == null ? (
                <p className="text-xs text-muted-foreground">
                  Save without an image to auto-generate a tile, or generate manually after saving.
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={closeModal} disabled={saving || aiImageGenerating}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || imageUploading || aiImageGenerating}>
              {saving ? 'Saving…' : editingId != null ? 'Save changes' : 'Create category'}
            </Button>
          </div>
        </form>
      </Modal>
    </Card>
  )
}
